/** Named JSON sample shown under the process-parameters textarea. */
export interface AssignmentParameterExample {
  titleKey: string;
  json: string;
}

/** Process-specific samples and field descriptions. Null means the generic date-placeholder hint. */
export interface AssignmentParameterHelp {
  examples: readonly AssignmentParameterExample[];
  parameterKeys: readonly string[];
}

const LOAD_ERA_RAW: AssignmentParameterHelp = {
  examples: [
    {
      titleKey: 'WEATHER_LOAD_ERA_RAW_EXAMPLE_SKIP_REPORTS',
      json: `{
  "StartDate": "{{today-7d}}",
  "EndDate": "{{today}}",
  "SkipReports": true,
  "Force": false
}`,
    },
    {
      titleKey: 'WEATHER_LOAD_ERA_RAW_EXAMPLE_FORCE',
      json: `{
  "StartDate": "2026-07-01",
  "EndDate": "2026-08-30",
  "SkipReports": false,
  "Force": true
}`,
    },
  ],
  parameterKeys: [
    'WEATHER_ERA_PARAM_START_DATE',
    'WEATHER_ERA_PARAM_END_DATE',
    'WEATHER_ERA_PARAM_SKIP_REPORTS',
    'WEATHER_ERA_PARAM_FORCE',
  ],
};

const MAINTAIN_ERA_RAW: AssignmentParameterHelp = {
  examples: [
    {
      titleKey: 'WEATHER_MAINTAIN_ERA_RAW_EXAMPLE_DEFAULT',
      json: `{}`,
    },
  ],
  parameterKeys: [],
};

const DOWNLOAD_ERA_NETCDF: AssignmentParameterHelp = {
  examples: [{
    titleKey: 'WEATHER_DOWNLOAD_ERA_NETCDF_EXAMPLE',
    json: `{
  "StartDate": "{{today-30d}}",
  "EndDate": "{{today-30d}}",
  "Force": false
}`,
  }],
  parameterKeys: ['WEATHER_ERA_PARAM_START_DATE', 'WEATHER_ERA_PARAM_END_DATE', 'WEATHER_ERA_PARAM_FORCE'],
};

const IMPORT_ERA_NETCDF: AssignmentParameterHelp = {
  examples: [{
    titleKey: 'WEATHER_IMPORT_ERA_NETCDF_EXAMPLE',
    json: `{
  "StartDate": "{{today-30d}}",
  "EndDate": "{{today-30d}}",
  "SkipReports": true
}`,
  }],
  parameterKeys: ['WEATHER_ERA_PARAM_START_DATE', 'WEATHER_ERA_PARAM_END_DATE', 'WEATHER_ERA_PARAM_SKIP_REPORTS'],
};

const CALCULATE_ERA_REPORTS: AssignmentParameterHelp = {
  examples: [
    {
      titleKey: 'WEATHER_CALCULATE_ERA_REPORTS_EXAMPLE',
      json: `{
  "StartDate": "{{today-7d}}",
  "EndDate": "{{today}}"
}`,
    },
  ],
  parameterKeys: [
    'WEATHER_ERA_PARAM_START_DATE',
    'WEATHER_ERA_PARAM_END_DATE',
  ],
};

const REFRESH_ERA_TILES: AssignmentParameterHelp = {
  examples: [
    {
      titleKey: 'WEATHER_REFRESH_ERA_TILES_EXAMPLE_DEFAULT',
      json: '{}',
    },
    {
      titleKey: 'WEATHER_REFRESH_ERA_TILES_EXAMPLE_RANGE',
      json: `{
  "StartDate": "2026-10-05",
  "EndDate": "2026-10-19"
}`,
    },
  ],
  parameterKeys: [
    'WEATHER_REFRESH_ERA_TILES_PARAMS',
  ],
};

const ERA_DEVIATION_TILES: AssignmentParameterHelp = {
  examples: [
    {
      titleKey: 'WEATHER_ERA_DEVIATION_TILES_EXAMPLE_DEFAULT',
      json: '{}',
    },
    {
      titleKey: 'WEATHER_ERA_DEVIATION_TILES_EXAMPLE_RANGE',
      json: `{
  "StartDate": "2026-10-06",
  "EndDate": "2026-10-14",
  "ForceRecreate": true
}`,
    },
  ],
  parameterKeys: [
    'WEATHER_ERA_DEVIATION_TILES_PARAMS',
  ],
};

const FILL_ERA_SAT: AssignmentParameterHelp = {
  examples: [
    {
      titleKey: 'WEATHER_FILL_ERA_SAT_EXAMPLE_DEFAULT',
      json: '{}',
    },
    {
      titleKey: 'WEATHER_FILL_ERA_SAT_EXAMPLE_RANGE',
      json: `{
  "StartDate": "2026-10-05",
  "EndDate": "2026-10-19"
}`,
    },
  ],
  parameterKeys: [
    'WEATHER_FILL_ERA_SAT_PARAMS',
  ],
};

const GENERATE_ERA_TILES: AssignmentParameterHelp = {
  examples: [
    {
      titleKey: 'WEATHER_GENERATE_ERA_TILES_EXAMPLE_STATION_CELLS',
      json: `{
  "StartDate": "{{today-7d}}",
  "EndDate": "{{today}}",
  "ForceRecreate": false,
  "RenderMode": "StationCells"
}`,
    },
    {
      titleKey: 'WEATHER_GENERATE_ERA_TILES_EXAMPLE_NEAREST',
      json: `{
  "StartDate": "2026-07-01",
  "EndDate": "2026-08-30",
  "ForceRecreate": true,
  "RenderMode": "NearestNeighbor"
}`,
    },
  ],
  parameterKeys: [
    'WEATHER_ERA_PARAM_START_DATE',
    'WEATHER_ERA_PARAM_END_DATE',
    'WEATHER_ERA_PARAM_FORCE_RECREATE',
    'WEATHER_ERA_PARAM_RENDER_MODE',
  ],
};

const DATA_QUALITY: AssignmentParameterHelp = {
  examples: [
    {
      titleKey: 'WEATHER_DATA_QUALITY_EXAMPLE_POINTS',
      json: `{
  "Points": [{ "Lat": 41.3, "Lng": 69.24 }],
  "StartDate": "{{today-30d}}",
  "EndDate": "{{today}}",
  "ForecastDaysAhead": 7
}`,
    },
    {
      titleKey: 'WEATHER_DATA_QUALITY_EXAMPLE_REGIONS',
      json: `{
  "RegionIds": ["00000000-0000-0000-0000-000000000000"]
}`,
    },
  ],
  parameterKeys: [
    'WEATHER_DATA_QUALITY_PARAM_POINTS',
    'WEATHER_DATA_QUALITY_PARAM_REGION_IDS',
    'WEATHER_DATA_QUALITY_PARAM_DATES',
    'WEATHER_DATA_QUALITY_PARAM_FORECAST_DAYS',
  ],
};

const TILE_QUALITY: AssignmentParameterHelp = {
  examples: [
    {
      titleKey: 'WEATHER_TILE_QUALITY_EXAMPLE_POINTS',
      json: `{
  "Points": [{ "Lat": 41.3, "Lng": 69.24 }],
  "StartDate": "{{today-30d}}",
  "EndDate": "{{today}}",
  "ForecastDaysAhead": 5,
  "RenderCheck": true
}`,
    },
    {
      titleKey: 'WEATHER_DATA_QUALITY_EXAMPLE_REGIONS',
      json: `{
  "RegionIds": ["00000000-0000-0000-0000-000000000000"],
  "RenderCheck": true
}`,
    },
  ],
  parameterKeys: [
    'WEATHER_DATA_QUALITY_PARAM_POINTS',
    'WEATHER_DATA_QUALITY_PARAM_REGION_IDS',
    'WEATHER_DATA_QUALITY_PARAM_DATES',
    'WEATHER_TILE_QUALITY_PARAM_FORECAST_DAYS',
    'WEATHER_TILE_QUALITY_PARAM_RENDER',
  ],
};

const IBM_HISTORICAL: AssignmentParameterHelp = {
  examples: [
    {
      titleKey: 'IBM_HISTORICAL_EXAMPLE_CURSOR',
      json: '{}',
    },
    {
      titleKey: 'IBM_HISTORICAL_EXAMPLE_RANGE',
      json: `{
  "StartDate": "{{today-30d}}",
  "EndDate": "{{today}}"
}`,
    },
  ],
  parameterKeys: ['IBM_HISTORICAL_PARAMS'],
};

const IBM_FORECAST: AssignmentParameterHelp = {
  examples: [{ titleKey: 'IBM_FORECAST_EXAMPLE', json: '{}' }],
  parameterKeys: ['IBM_FORECAST_PARAMS'],
};

const IBM_REPORTS: AssignmentParameterHelp = {
  examples: [
    { titleKey: 'IBM_REPORTS_EXAMPLE', json: '{}' },
    {
      titleKey: 'IBM_REPORTS_EXAMPLE_RANGE',
      json: `{
  "StartDate": "2017-01-01",
  "EndDate": "2018-12-31",
  "SkipAggregates": false
}`,
    },
  ],
  parameterKeys: ['IBM_REPORTS_PARAMS'],
};

const IBM_TILES: AssignmentParameterHelp = {
  examples: [
    {
      titleKey: 'IBM_TILES_EXAMPLE_FORECAST',
      json: '{}',
    },
    {
      titleKey: 'IBM_TILES_EXAMPLE_RANGE',
      json: `{
  "StartDate": "2026-07-01",
  "EndDate": "2026-08-30",
  "ForceRecreate": true
}`,
    },
  ],
  parameterKeys: ['IBM_TILES_PARAMS'],
};

const COUNTRY_RGB_LAYERS: AssignmentParameterHelp = {
  examples: [
    {
      titleKey: 'COUNTRY_RGB_LAYERS_EXAMPLE_SENTINEL',
      json: `{
  "ProductSourceType": "Sentinel2",
  "PublishOnly": false,
  "Overwrite": false,
  "Take": 50
}`,
    },
    {
      titleKey: 'COUNTRY_RGB_LAYERS_EXAMPLE_LANDSAT_PUBLISH',
      json: `{
  "ProductSourceType": "Landsat",
  "PublishOnly": true
}`,
    },
    {
      titleKey: 'COUNTRY_RGB_LAYERS_EXAMPLE_RANGE',
      json: `{
  "ProductSourceType": "Sentinel2",
  "StartDate": "2026-10-05",
  "EndDate": "2026-10-19"
}`,
    },
  ],
  parameterKeys: [
    'COUNTRY_RGB_PARAM_SOURCE',
    'COUNTRY_RGB_LAYERS_PARAM_DATES',
    'COUNTRY_RGB_LAYERS_PARAM_PUBLISH_ONLY',
    'COUNTRY_RGB_LAYERS_PARAM_OVERWRITE',
    'COUNTRY_RGB_LAYERS_PARAM_TAKE',
  ],
};

const COUNTRY_RGB_MOSAIC: AssignmentParameterHelp = {
  examples: [
    {
      titleKey: 'COUNTRY_RGB_MOSAIC_EXAMPLE_SENTINEL_YEAR',
      json: `{
  "ProductSourceType": "Sentinel2",
  "Year": 2024
}`,
    },
    {
      titleKey: 'COUNTRY_RGB_MOSAIC_EXAMPLE_LANDSAT_ALL_YEARS',
      json: `{
  "ProductSourceType": "Landsat"
}`,
    },
    {
      titleKey: 'COUNTRY_RGB_MOSAIC_EXAMPLE_SENTINEL_CURRENT_YEAR',
      json: `{
  "ProductSourceType": "Sentinel2",
  "CurrentYearOnly": true
}`,
    },
  ],
  parameterKeys: [
    'COUNTRY_RGB_PARAM_SOURCE',
    'COUNTRY_RGB_MOSAIC_PARAM_YEAR',
    'COUNTRY_RGB_MOSAIC_PARAM_CURRENT_YEAR_ONLY',
  ],
};

const EMPTY_WEATHER: AssignmentParameterHelp = {
  examples: [{ titleKey: 'WEATHER_EMPTY_PARAMETERS_EXAMPLE', json: '{}' }],
  parameterKeys: ['WEATHER_EMPTY_PARAMETERS_HINT'],
};

const NO_INPUT: AssignmentParameterHelp = {
  examples: [{ titleKey: 'PROCESS_NO_PARAMETERS_EXAMPLE', json: '{}' }],
  parameterKeys: ['PROCESS_NO_PARAMETERS_HINT'],
};

function sample(titleKey: string, json: string, parameterKeys: readonly string[]): AssignmentParameterHelp {
  return { examples: [{ titleKey, json }], parameterKeys };
}

function samples(
  examples: readonly AssignmentParameterExample[],
  parameterKeys: readonly string[],
): AssignmentParameterHelp {
  return { examples, parameterKeys };
}

const FIND_PRODUCTS = samples(
  [
    {
      titleKey: 'SATELLITE_FIND_PRODUCTS_EXAMPLE',
      json: `{
  "Name": "Scheduled satellite search",
  "Geometry": { "SourceType": "CountryBoundaries" },
  "Providers": "All",
  "Priority": "Normal",
  "StartDate": "{{today-7d}}",
  "EndDate": "{{today}}",
  "Lookback": "2.00:00:00"
}`,
    },
  ],
  ['SATELLITE_FIND_PRODUCTS_PARAMS'],
);

const DOWNLOAD_PRODUCTS = samples(
  [
    {
      titleKey: 'SATELLITE_DOWNLOAD_PRODUCTS_EXAMPLE',
      json: `{
  "Name": "Scheduled satellite download",
  "Geometry": { "SourceType": "CountryBoundaries" },
  "Providers": "All",
  "Priority": "Normal",
  "StartDate": "{{today-7d}}",
  "EndDate": "{{today}}",
  "MaximumCloudCover": 100
}`,
    },
  ],
  ['SATELLITE_DOWNLOAD_PRODUCTS_PARAMS'],
);

const POST_PROCESSING = sample(
  'SATELLITE_POST_PROCESSING_EXAMPLE',
  `{
  "SatelliteProductId": "00000000-0000-0000-0000-000000000000",
  "Priority": "Normal"
}`,
  ['SATELLITE_POST_PROCESSING_PARAMS'],
);

const CATCH_UP = sample(
  'SATELLITE_CATCH_UP_EXAMPLE',
  `{
  "Take": 50
}`,
  ['SATELLITE_CATCH_UP_PARAMS'],
);

const REFRESH_METADATA = sample(
  'SATELLITE_REFRESH_METADATA_EXAMPLE',
  `{
  "Take": 10
}`,
  ['SATELLITE_REFRESH_METADATA_PARAMS'],
);

const INDEX_TIFF = samples(
  [
    {
      titleKey: 'SATELLITE_INDEX_TIFF_EXAMPLE_DEFAULT',
      json: `{
  "ProductSourceType": "Sentinel2"
}`,
    },
    {
      titleKey: 'SATELLITE_INDEX_TIFF_EXAMPLE_ROOT',
      json: `{
  "ProductSourceType": "Landsat",
  "IndexRoot": "/data/instances/masofa/data/geoserver/indexes"
}`,
    },
  ],
  ['COUNTRY_RGB_PARAM_SOURCE', 'SATELLITE_INDEX_TIFF_PARAM_ROOT'],
);

const SEASON_INDICES = samples(
  [
    { titleKey: 'SEASON_INDICES_EXAMPLE_QUEUE', json: '{}' },
    {
      titleKey: 'SEASON_INDICES_EXAMPLE_FIELD',
      json: `{
  "FieldId": "00000000-0000-0000-0000-000000000000",
  "DateFrom": "{{today-30d}}",
  "DateTo": "{{today}}"
}`,
    },
  ],
  ['SEASON_INDICES_PARAMS'],
);

const BACKFILL = samples(
  [
    { titleKey: 'SEASON_BACKFILL_EXAMPLE_CURSOR', json: '{}' },
    {
      titleKey: 'SEASON_BACKFILL_EXAMPLE_WINDOW',
      json: `{
  "DateFrom": "2024-01-01",
  "DateTo": "2024-12-31",
  "Force": true,
  "PolygonDetectionJobId": "00000000-0000-0000-0000-000000000000"
}`,
    },
  ],
  ['SEASON_BACKFILL_PARAMS'],
);

const METADATA_TAKE = sample('SATELLITE_METADATA_TAKE_EXAMPLE', '{ "Take": 100 }', ['SATELLITE_METADATA_TAKE_PARAMS']);
const CONVERT_TILES = sample('SATELLITE_CONVERT_TILES_EXAMPLE', '{ "Take": 100 }', ['SATELLITE_CONVERT_TILES_PARAMS']);
const LANDSAT_FIND = sample(
  'SATELLITE_FIND_LANDSAT_EXAMPLE',
  `{
  "StartDate": "{{today-5d}}",
  "EndDate": "{{today}}"
}`,
  ['SATELLITE_FIND_LANDSAT_PARAMS'],
);
const SHARED_INDEX_REPORTS = sample(
  'SATELLITE_SHARED_INDEX_REPORTS_EXAMPLE',
  '{ "WindowDays": 30 }',
  ['SATELLITE_SHARED_INDEX_REPORTS_PARAMS'],
);

const SEGMENTATION = sample(
  'AI_SEGMENTATION_EXAMPLE',
  `{
  "Polygon": { "type": "Polygon", "coordinates": [] }
}`,
  ['AI_SEGMENTATION_PARAMS'],
);
const REGION_ID = sample(
  'AI_REGION_EXAMPLE',
  `{
  "RegionId": "00000000-0000-0000-0000-000000000000"
}`,
  ['AI_REGION_PARAMS'],
);
const ANOMALIES = samples(
  [
    { titleKey: 'AI_ANOMALIES_EXAMPLE_YESTERDAY', json: '{}' },
    {
      titleKey: 'AI_ANOMALIES_EXAMPLE_DATE',
      json: `{
  "Date": "{{today-1d}}",
  "ModelId": "00000000-0000-0000-0000-000000000000"
}`,
    },
  ],
  ['AI_ANOMALIES_PARAMS'],
);
const RECURRING = samples(
  [
    { titleKey: 'RECURRING_ANOMALIES_EXAMPLE_ALL', json: '{}' },
    {
      titleKey: 'RECURRING_ANOMALIES_EXAMPLE_FIELD',
      json: `{
  "FieldId": "00000000-0000-0000-0000-000000000000",
  "DetectionYear": 2026
}`,
    },
  ],
  ['RECURRING_ANOMALIES_PARAMS'],
);
const CLASSIFY_ONNX = sample(
  'AI_CLASSIFY_ONNX_EXAMPLE',
  `{
  "PolygonDetectionJobId": "00000000-0000-0000-0000-000000000000",
  "BatchSize": 250
}`,
  ['AI_CLASSIFY_ONNX_PARAMS'],
);
const FIELDSEG_NET = sample(
  'AI_FIELDSEG_NET_EXAMPLE_DAY',
  `{
  "DateFrom": "2026-10-01",
  "DateTo": "2026-10-01",
  "Overwrite": false
}`,
  ['AI_FIELDSEG_NET_PARAMS'],
);
const FARMER_REPORTS = sample(
  'FARMER_REPORTS_EXAMPLE',
  `{
  "MaxReportsPerRun": 30,
  "AlsoPdf": false
}`,
  ['FARMER_REPORTS_PARAMS'],
);
const BID_TEMPLATES = sample(
  'BID_TEMPLATES_EXAMPLE',
  `{
  "FromUtc": "{{today-1d}}",
  "ToUtc": "{{today}}",
  "Locale": "ru-RU",
  "Bucket": "bid-template-exports"
}`,
  ['BID_TEMPLATES_PARAMS'],
);
const PLANT_DIAG = sample(
  'PLANT_DIAG_EXAMPLE',
  `{
  "MaxReportsPerRun": 50,
  "MaxDurationSeconds": 120
}`,
  ['PLANT_DIAG_PARAMS'],
);
const CROP_AGRO = sample(
  'CROP_AGRO_EXAMPLE',
  `{
  "BatchSize": 500,
  "MaxBatches": 10
}`,
  ['CROP_AGRO_PARAMS'],
);
const INDEX_LAYER_CHECK = sample(
  'INDEX_LAYER_CHECK_EXAMPLE',
  `{
  "DateFrom": "{{today-14d}}",
  "DateTo": "{{today}}",
  "IndexTypes": ["NDVI"],
  "RenderCheck": true
}`,
  ['INDEX_LAYER_CHECK_PARAMS'],
);
const PROBE = sample('ORCHESTRATION_PROBE_EXAMPLE', '{ "FailEvery": 2 }', ['ORCHESTRATION_PROBE_PARAMS']);

const NO_INPUT_KEYS = [
  'satellite.reset-stale-work',
  'satellite.repair-products',
  'satellite.index-tile-map-backfill',
  'satellite.calculate-index-layers',
  'satellite.build-sentinel-preview',
  'ai.transfer-yield-forecast-to-seasons',
  'ai.run-crop-classification',
  'analitic.submit-bids-to-qwen',
  'analitic.fetch-qwen-results',
  'analitic.collect-qwen-full-analysis',
  'analitic.start-qwen-analysis',
  'analitic.request-farmer-report-for-finished-bids',
  'analitic.request-farmer-report-for-satellite-anomalies',
  'crop.sync-cadastre-parcels',
  'uav.pull-sources',
  'system.create-partitions',
  'system.health-check',
  'system.process-due-user-notifications',
] as const;

const BY_KEY: Readonly<Record<string, AssignmentParameterHelp>> = {
  ...Object.fromEntries(NO_INPUT_KEYS.map(key => [key, NO_INPUT])),
  'weather.load-era-raw': LOAD_ERA_RAW,
  'weather.maintain-era-raw': MAINTAIN_ERA_RAW,
  'weather.download-era-netcdf': DOWNLOAD_ERA_NETCDF,
  'weather.import-era-netcdf': IMPORT_ERA_NETCDF,
  'weather.calculate-era-reports': CALCULATE_ERA_REPORTS,
  'weather.fill-era-sat': FILL_ERA_SAT,
  'weather.refresh-era-report-tiles': REFRESH_ERA_TILES,
  'weather.generate-era-deviation-tiles': ERA_DEVIATION_TILES,
  'weather.generate-era-tiles': GENERATE_ERA_TILES,
  'weather.check-ibm-day-data': DATA_QUALITY,
  'weather.check-era5-day-data': DATA_QUALITY,
  'weather.check-ibm-tiles': TILE_QUALITY,
  'weather.check-era5-tiles': TILE_QUALITY,
  'weather.load-ibm-historical': IBM_HISTORICAL,
  'weather.load-ibm-forecast': IBM_FORECAST,
  'weather.calculate-ibm-reports': IBM_REPORTS,
  'weather.generate-ibm-report-tiles': IBM_TILES,
  'satellite.country-rgb-layers': COUNTRY_RGB_LAYERS,
  'satellite.country-rgb-mosaic': COUNTRY_RGB_MOSAIC,
  'satellite.find-products': FIND_PRODUCTS,
  'satellite.download-found-products': DOWNLOAD_PRODUCTS,
  'satellite.measure-download-backlog': DOWNLOAD_PRODUCTS,
  'satellite.create-product-postprocessing': POST_PROCESSING,
  'satellite.catch-up-product-postprocessing': CATCH_UP,
  'satellite.refresh-copernicus-metadata': REFRESH_METADATA,
  'satellite.index-tiff-ingest': INDEX_TIFF,
  'satellite.calculate-season-indices': SEASON_INDICES,
  'satellite.backfill-season-indices': BACKFILL,
  'satellite.load-sentinel-metadata': METADATA_TAKE,
  'satellite.convert-sentinel-tiles': CONVERT_TILES,
  'satellite.find-landsat-products': LANDSAT_FIND,
  'satellite.build-shared-index-reports': SHARED_INDEX_REPORTS,
  'ai.start-segmentation': SEGMENTATION,
  'ai.identify-crops': REGION_ID,
  'ai.calculate-yield-forecast': REGION_ID,
  'ai.calculate-anomalies': ANOMALIES,
  'indices.detect-recurring-anomalies': RECURRING,
  'ai.classify-crops-onnx': CLASSIFY_ONNX,
  'ai.field-segmentation-net': FIELDSEG_NET,
  'analitic.generate-farmer-reports': FARMER_REPORTS,
  'crop.export-bid-templates': BID_TEMPLATES,
  'analitic.collect-plant-diag-results': PLANT_DIAG,
  'crop.load-crop-agro-initial': CROP_AGRO,
  'crop.apply-crop-agro-lands': CROP_AGRO,
  'satellite.check-index-layers-data': INDEX_LAYER_CHECK,
  'orchestration.probe': PROBE,
};

/** Process keys listed on /admin/orchestration/processes (geometry is filtered out of that page). */
export const PROCESSES_PAGE_KEYS = [
  'satellite.find-products',
  'satellite.download-found-products',
  'satellite.measure-download-backlog',
  'satellite.create-product-postprocessing',
  'satellite.catch-up-product-postprocessing',
  'satellite.reset-stale-work',
  'satellite.repair-products',
  'satellite.refresh-copernicus-metadata',
  'satellite.country-rgb-layers',
  'satellite.country-rgb-mosaic',
  'satellite.index-tiff-ingest',
  'satellite.index-tile-map-backfill',
  'satellite.calculate-season-indices',
  'satellite.calculate-index-layers',
  'satellite.backfill-season-indices',
  'weather.load-era-raw',
  'weather.download-era-netcdf',
  'weather.import-era-netcdf',
  'weather.calculate-era-reports',
  'weather.generate-era-tiles',
  'weather.maintain-era-raw',
  'weather.reconcile-era-reports',
  'weather.refresh-era-report-tiles',
  'weather.fill-era-sat',
  'weather.update-era-normalized',
  'weather.generate-era-deviation-tiles',
  'weather.generate-era-yearly-atd-tiles',
  'weather.fill-era-deviation-2020',
  'weather.load-ibm-historical',
  'weather.load-ugm-current',
  'weather.load-ugm-forecast',
  'ai.start-segmentation',
  'ai.identify-crops',
  'ai.calculate-yield-forecast',
  'ai.calculate-anomalies',
  'ai.transfer-yield-forecast-to-seasons',
  'ai.run-crop-classification',
  'ai.field-segmentation-net',
  'analitic.generate-farmer-reports',
  'crop.export-bid-templates',
  'analitic.submit-bids-to-qwen',
  'analitic.fetch-qwen-results',
  'analitic.collect-qwen-full-analysis',
  'analitic.start-qwen-analysis',
  'analitic.request-farmer-report-for-finished-bids',
  'analitic.request-farmer-report-for-satellite-anomalies',
  'analitic.collect-plant-diag-results',
  'crop.sync-cadastre-parcels',
  'uav.pull-sources',
  'orchestration.probe',
] as const;

/**
 * Samples shown under the assignment and subscription start form.
 * Weather keys without their own entry share the empty-object hint.
 */
export function assignmentParameterHelp(processKey: string | null | undefined): AssignmentParameterHelp | null {
  const key = (processKey ?? '').trim();
  if (!key) {
    return null;
  }
  return BY_KEY[key] ?? (key.startsWith('weather.') ? EMPTY_WEATHER : null);
}
